import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@/api/client'
import type { CanonicalEvent } from '@/lib/runs'

/** Append live pages; replace expired URLs without clearing the player's selection. */
export function useRolloutEvents(runId: string, jobId: string, live: boolean, attemptId?: string) {
  const [events, setEvents] = useState<CanonicalEvent[]>([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  const [refresh, setRefresh] = useState(0)
  const refreshed = useRef(0)
  useEffect(() => {
    setEvents([])
    setLoaded(false)
    setError('')
    refreshed.current = 0
  }, [runId, jobId, attemptId])

  useEffect(() => {
    let cancelled = false
    let timer: number | undefined
    let after = 0
    // REMI follows a job's newest attempt; when a retry starts one, read it from its start.
    let attempt: string | null | undefined
    async function poll() {
      try {
        if (document.hidden) return
        // Drain saved history before waiting for another live update.
        while (!cancelled) {
          const value = await api.get<{ events: CanonicalEvent[]; attempt_id?: string | null }>(
            `/eng/runs/${runId}/rollouts/${jobId}/events`, { after, attempt: attemptId },
          )
          if (cancelled) return
          setLoaded(true)
          setError('')
          if (value.attempt_id !== undefined && value.attempt_id !== attempt) {
            const changed = attempt !== undefined
            attempt = value.attempt_id
            if (changed) {
              setEvents([])
              after = 0
              continue
            }
          }
          if (!value.events.length) break
          setEvents(previous => [...new Map([...previous, ...value.events]
            .map(event => [event.sequence, event])).values()].sort((a, b) => a.sequence - b.sequence))
          const next = Math.max(...value.events.map(event => event.sequence))
          if (next <= after) break
          after = next
        }
      } catch (error) {
        if (!cancelled) { setError((error as Error).message); setLoaded(true) }
      } finally {
        if (!cancelled && live) timer = window.setTimeout(poll, 3000)
      }
    }
    void poll()
    const visible = () => { if (!document.hidden && !live) { setRefresh(value => value + 1) } }
    document.addEventListener('visibilitychange', visible)
    return () => { cancelled = true; window.clearTimeout(timer); document.removeEventListener('visibilitychange', visible) }
  }, [runId, jobId, live, refresh, attemptId])

  const refreshUrls = useCallback(() => {
    if (Date.now() - refreshed.current < 30_000) return
    refreshed.current = Date.now()
    setRefresh(value => value + 1)
  }, [])
  return { events, loaded, error, refreshUrls }
}
